import { FunctionComponent, ReactNode, useState } from "react";
import { useForm } from "react-hook-form";

import { classNames } from "@/utils/classNames";

import { ContentStatus } from "../schema/knowledgeDb";

interface ContentSectionProps {
	title: string;
	children: ReactNode;
	/** Raw text value for edit mode (used for simple text sections) */
	editValue?: string;
	onSave?: (value: string) => void;
	onDelete?: () => void;
	/** If true, shows "Read more" truncation */
	collapsible?: boolean;
	/** Current content status for this section */
	contentStatus?: ContentStatus;
	/** Called when user clicks the approve button */
	onApprove?: () => void;
	/** Called when user clicks the unapprove button */
	onUnapprove?: () => void;
}

interface EditForm {
	content: string;
}

const SectionStatusBadge: FunctionComponent<{ status: ContentStatus }> = ({ status }) => {
	if (status === ContentStatus.AI_DRAFTED) {
		return (
			<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-brand-white border border-blue-action/40 text-blue-action">
				✨ AI
			</span>
		);
	}

	if (status === ContentStatus.DRAFT) {
		return (
			<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-status-warning-light border border-status-warning/40 text-status-warning">
				✎ Draft
			</span>
		);
	}

	if (status === ContentStatus.HUMAN_APPROVED) {
		return (
			<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-status-success-light border border-status-success/40 text-status-success">
				✓ Approved
			</span>
		);
	}

	return null;
};

export const ContentSection: FunctionComponent<ContentSectionProps> = ({
	title,
	children,
	editValue,
	onSave,
	onDelete,
	collapsible = false,
	contentStatus,
	onApprove,
	onUnapprove,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);

	const { register, handleSubmit, reset } = useForm<EditForm>({
		defaultValues: { content: editValue ?? "" },
	});

	const handleEdit = () => {
		reset({ content: editValue ?? "" });
		setIsEditing(true);
	};

	const handleCancel = () => {
		setIsEditing(false);
		reset({ content: editValue ?? "" });
	};

	const onSubmit = (data: EditForm) => {
		onSave?.(data.content);
		setIsEditing(false);
		// Auto-approve on manual edit
		if (contentStatus && contentStatus !== ContentStatus.HUMAN_APPROVED) {
			onApprove?.();
		}
	};

	return (
		<div className="rounded-xl border border-gray-300 bg-white mb-4">
			{/* Section header */}
			<div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
				<div className="flex items-center gap-2">
					<h3 className="text-sm font-semibold text-gray-900">{title}</h3>
					{contentStatus && <SectionStatusBadge status={contentStatus} />}
				</div>
				{!isEditing && (
					<div className="flex items-center gap-2">
						{onApprove &&
							(contentStatus === ContentStatus.AI_DRAFTED ||
								contentStatus === ContentStatus.DRAFT) && (
								<button
									className="text-xs font-medium cursor-pointer text-status-warning hover:text-status-warning/80 px-2 py-1 rounded transition-colors"
									type="button"
									onClick={onApprove}
								>
									✓ Approve
								</button>
							)}
						{onUnapprove && contentStatus === ContentStatus.HUMAN_APPROVED && (
							<button
								className="text-xs font-medium cursor-pointer text-status-warning hover:text-status-warning/80 px-2 py-1 rounded transition-colors"
								type="button"
								onClick={onUnapprove}
							>
								✗ Unapprove
							</button>
						)}
						{onSave && editValue !== undefined && (
							<button
								className="text-xs font-medium cursor-pointer text-text-meta hover:text-blue-action px-2 py-1 rounded transition-colors"
								type="button"
								onClick={handleEdit}
							>
								Edit
							</button>
						)}
						{onDelete && (
							<button
								className="text-xs font-medium cursor-pointer text-red-500 hover:text-red-500/80 px-2 py-1 rounded transition-colors"
								type="button"
								onClick={onDelete}
							>
								Delete
							</button>
						)}
					</div>
				)}
			</div>

			{/* Section body */}
			<div className="px-4 py-3">
				{isEditing ? (
					<form onSubmit={handleSubmit(onSubmit)}>
						<textarea
							className="w-full min-h-[120px] p-3 text-sm border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
							{...register("content")}
						/>
						<div className="flex justify-end gap-2 mt-2">
							<button
								className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
								type="button"
								onClick={handleCancel}
							>
								Cancel
							</button>
							<button
								className="px-3 py-1.5 text-xs font-medium text-white bg-brand-light rounded-lg hover:bg-blue-action transition-colors"
								type="submit"
							>
								Save
							</button>
						</div>
					</form>
				) : (
					<div>
						<div
							className={classNames(
								"text-sm text-gray-700 leading-relaxed",
								collapsible && !isExpanded ? "line-clamp-3" : ""
							)}
						>
							{children}
						</div>
						{collapsible && !isExpanded && (
							<button
								className="mt-1 text-xs font-medium text-brand-light hover:text-blue-action transition-colors"
								type="button"
								onClick={() => setIsExpanded(true)}
							>
								Read more
							</button>
						)}
						{collapsible && isExpanded && (
							<button
								className="mt-1 text-xs font-medium text-brand-light hover:text-blue-action transition-colors"
								type="button"
								onClick={() => setIsExpanded(false)}
							>
								Show less
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
